import Service, { type Registry as Services, service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';
import type { BudgetForecastDataItem } from 'lumen/models';
import { markItemsAsNeedsForceDistribution } from 'lumen/utils/budget-forecast/force-distribution';

export function groupItemsBySegmentKey(
  items: BudgetForecastDataItem[],
): Record<string, BudgetForecastDataItem[]> {
  return items.reduce<Record<string, BudgetForecastDataItem[]>>((acc, item) => {
    const key = item.segmentKey;

    if (!acc[key]) {
      acc[key] = [];
    }

    acc[key].push(item);

    return acc;
  }, {});
}

export default class BudgetForecastService extends Service {
  @service declare router: Services['router'];

  @tracked items: BudgetForecastDataItem[] = [];

  @cached
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get editableItems() {
    const { view } = this;
    return this.items.filter((item) => item.dataType === view);
  }

  @cached
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get editableItemsGroupedBySegmentKey() {
    return groupItemsBySegmentKey(this.editableItems);
  }

  @cached
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get editableItemsWithoutHidden() {
    return this.editableItems.filter((item) => !item.isItemHidden);
  }

  @cached
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get itemsToBeForceDistributed() {
    return this.editableItems.filter((item) => item.needsForceDistribution);
  }

  @cached
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get itemsWithUndistributedChanges() {
    return this.editableItems.filter((item) => item.hasUndistributedChanges);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get view() {
    return this.router.currentRouteName === 'hotel.settings.budget.detail'
      ? 'budget'
      : 'forecast';
  }

  setAndRollbackBudgetForecastItems(items: BudgetForecastDataItem[]): void {
    this.items = items;
    this.items.forEach((item) => item.rollbackAttributes());
    markItemsAsNeedsForceDistribution(this.editableItems);
  }
}

declare module '@ember/service' {
  interface Registry {
    'budget-forecast': BudgetForecastService;
  }
}
